import { makeEnvelope } from './envelope';
import type { Send, WireIME } from './types';

export interface IMEHandle {
  /** 隐藏输入元素。调用方可在 video 被点击时调 `focusHidden()` 让 IME 生效。 */
  readonly inputEl: HTMLInputElement;
  /** 触发 inputEl.focus(),让 IME 组合串映射到这里。 */
  focusHidden: () => void;
  /** 清理:移除 listener + DOM 节点。 */
  stop: () => void;
}

// attachIME 通过一个隐藏 <input> 捕获输入法合成事件并转发。
//
// 浏览器原生 compositionstart/update/end 只在具备输入焦点的可编辑元素上触发,
// 所以要造一个不可见但可聚焦的 input,并在 video 获得用户点击时把焦点转过去。
export function attachIME(send: Send): IMEHandle {
  const inputEl = document.createElement('input');
  inputEl.type = 'text';
  inputEl.autocapitalize = 'off';
  inputEl.autocomplete = 'off';
  inputEl.spellcheck = false;
  inputEl.setAttribute('aria-hidden', 'true');
  inputEl.setAttribute('tabindex', '-1');
  Object.assign(inputEl.style, {
    position: 'fixed',
    top: '-1000px',
    left: '-1000px',
    width: '1px',
    height: '1px',
    opacity: '0',
    pointerEvents: 'none',
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(inputEl);

  let composing = false;

  const onCompStart = () => {
    composing = true;
    send(makeEnvelope<WireIME>('input.ime', { type: 'setComposition', text: '' }));
  };
  const onCompUpdate = (e: Event) => {
    const ce = e as CompositionEvent;
    const text = ce.data ?? '';
    send(
      makeEnvelope<WireIME>('input.ime', {
        type: 'setComposition',
        text,
        selection_start: text.length,
        selection_end: text.length,
      }),
    );
  };
  const onCompEnd = (e: Event) => {
    composing = false;
    const ce = e as CompositionEvent;
    const text = ce.data ?? '';
    if (text.length > 0) {
      send(makeEnvelope<WireIME>('input.ime', { type: 'commit', text }));
    }
    inputEl.value = '';
  };
  const onInput = () => {
    // 非合成态输入(粘贴 / emoji picker / 平台原生手写板)立刻提交。
    if (composing) return;
    const text = inputEl.value;
    if (text.length > 0) {
      send(makeEnvelope<WireIME>('input.ime', { type: 'commit', text }));
      inputEl.value = '';
    }
  };

  inputEl.addEventListener('compositionstart', onCompStart);
  inputEl.addEventListener('compositionupdate', onCompUpdate);
  inputEl.addEventListener('compositionend', onCompEnd);
  inputEl.addEventListener('input', onInput);

  return {
    inputEl,
    focusHidden: () => inputEl.focus(),
    stop: () => {
      inputEl.removeEventListener('compositionstart', onCompStart);
      inputEl.removeEventListener('compositionupdate', onCompUpdate);
      inputEl.removeEventListener('compositionend', onCompEnd);
      inputEl.removeEventListener('input', onInput);
      inputEl.remove();
    },
  };
}
